import type { ToolType } from '../store/editorStore'

export type EditorCommandId =
  | 'palette.open'
  | 'tool.select'
  | 'tool.draw_tray'
  | 'tool.draw_zone'
  | 'tool.erase'
  | 'tool.measure'
  | 'history.undo'
  | 'history.redo'
  | 'selection.select_all'
  | 'selection.duplicate'
  | 'selection.copy'
  | 'selection.paste'
  | 'selection.delete'
  | 'operation.rows'
  | 'operation.setup'
  | 'operation.allocation'
  | 'operation.pathways'
  | 'draft.save'
  | 'draft.publish'

export type EditorCommandContext = {
  canEdit: boolean
  canPublish: boolean
  selectedCount: number
  mode: ToolType
  canUndo: boolean
  canRedo: boolean
  hasClipboard: boolean
}

export type EditorCommand = {
  id: EditorCommandId
  label: string
  description: string
  shortcut?: string
  group: 'navigation' | 'mode' | 'history' | 'selection' | 'operations' | 'draft'
  dangerous?: boolean
  visible: boolean
  enabled: boolean
}

type CommandDefinition = Omit<EditorCommand, 'visible' | 'enabled'> & {
  visible: (context: EditorCommandContext) => boolean
  enabled: (context: EditorCommandContext) => boolean
}

const DEFINITIONS: CommandDefinition[] = [
  { id: 'palette.open', label: 'Открыть палитру команд', description: 'Найти действие по названию или сочетанию клавиш', shortcut: 'Ctrl+K', group: 'navigation', visible: () => true, enabled: () => true },
  { id: 'tool.select', label: 'Выбор', description: 'Выбирать и перемещать объекты', shortcut: 'V', group: 'mode', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'tool.draw_tray', label: 'Рисовать трассу', description: 'Прокладывать кабельный лоток', shortcut: 'D', group: 'mode', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'tool.draw_zone', label: 'Рисовать зону', description: 'Добавлять горячий или холодный коридор', shortcut: 'Z', group: 'mode', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'tool.erase', label: 'Ластик', description: 'Удалять выбранные объекты из черновика', shortcut: 'E', group: 'mode', dangerous: true, visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'tool.measure', label: 'Измерить', description: 'Измерять прямую или ортогональную длину на плане', shortcut: 'L', group: 'mode', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'history.undo', label: 'Отменить', description: 'Отменить последнее изменение черновика', shortcut: 'Ctrl+Z', group: 'history', visible: () => true, enabled: c => c.canEdit && c.canUndo },
  { id: 'history.redo', label: 'Повторить', description: 'Повторить отменённое изменение', shortcut: 'Ctrl+Y', group: 'history', visible: () => true, enabled: c => c.canEdit && c.canRedo },
  { id: 'selection.select_all', label: 'Выбрать все стойки', description: 'Выбрать все доступные стойки на плане', shortcut: 'Ctrl+A', group: 'selection', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'selection.duplicate', label: 'Дублировать выделенное', description: 'Создать копию выбранных стоек в черновике', shortcut: 'Ctrl+D', group: 'selection', visible: c => c.canEdit && c.selectedCount > 0, enabled: c => c.canEdit && c.selectedCount > 0 },
  { id: 'selection.copy', label: 'Скопировать выделенное', description: 'Положить выбранные стойки во внутренний буфер', shortcut: 'Ctrl+C', group: 'selection', visible: c => c.canEdit && c.selectedCount > 0, enabled: c => c.canEdit && c.selectedCount > 0 },
  { id: 'selection.paste', label: 'Вставить из буфера', description: 'Добавить копию стоек в текущий черновик', shortcut: 'Ctrl+V', group: 'selection', visible: c => c.canEdit && c.hasClipboard, enabled: c => c.canEdit && c.hasClipboard },
  { id: 'selection.delete', label: 'Удалить выделенное', description: 'Удалить объекты только из текущего черновика', shortcut: 'Delete', group: 'selection', dangerous: true, visible: c => c.canEdit && c.selectedCount > 0, enabled: c => c.canEdit && c.selectedCount > 0 },
  { id: 'operation.rows', label: 'Генератор рядов', description: 'Создать или изменить ряды и коридоры', shortcut: 'R', group: 'operations', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'operation.setup', label: 'Мастер настройки зала', description: 'Заполнить план по матричному шаблону', shortcut: 'W', group: 'operations', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'operation.allocation', label: 'Распределение стоек', description: 'Пакетно назначить стойки группам и владельцам', shortcut: 'M', group: 'operations', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'operation.pathways', label: 'Генератор магистралей', description: 'Создать кабельные трассы по правилам зала', shortcut: 'P', group: 'operations', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'draft.save', label: 'Сохранить черновик', description: 'Сохранить изменения без публикации', shortcut: 'Ctrl+S', group: 'draft', visible: c => c.canEdit, enabled: c => c.canEdit },
  { id: 'draft.publish', label: 'Опубликовать план', description: 'Применить проверенный черновик к рабочей модели', group: 'draft', visible: c => c.canPublish, enabled: c => c.canPublish },
]

export function getEditorCommands(context: EditorCommandContext): EditorCommand[] {
  return DEFINITIONS
    .filter(definition => definition.visible(context))
    .map(({ visible, enabled, ...definition }) => ({
      ...definition,
      visible: visible(context),
      enabled: enabled(context),
    }))
}

export function getContextEditorCommands(context: EditorCommandContext): EditorCommand[] {
  const commands = getEditorCommands(context)
  if (!context.canEdit) return commands.filter(command => command.id === 'palette.open')
  const ids = context.mode === 'select'
    ? ['selection.duplicate', 'selection.copy', 'selection.paste', 'selection.delete', 'operation.rows', 'operation.pathways', 'history.undo', 'palette.open']
    : ['tool.select', 'tool.measure', 'history.undo', 'palette.open']
  return commands.filter(command => ids.includes(command.id))
}

export function filterEditorCommands(commands: EditorCommand[], query: string): EditorCommand[] {
  const normalized = query.trim().toLocaleLowerCase()
  if (!normalized) return commands
  return commands.filter(command =>
    `${command.label} ${command.description} ${command.shortcut || ''}`.toLocaleLowerCase().includes(normalized),
  )
}
